
import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';
import { PRODUCTS, CATEGORIES } from './constants';
import { BlogPost, GenerationSettings, SavedPost, Product } from './types';
import { generateBlogContent, getTrendingTopics } from './services/gemini';
import Header from './components/Header';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'studio' | 'saved'>('studio');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  
  const [settings, setSettings] = useState<GenerationSettings>({
    category: 'Cleaning',
    topic: '',
    length: 'Medium',
    tone: 'Enthusiastic',
    angle: 'Deep Dive',
    selectedProductIds: []
  });
  
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [savedPosts, setSavedPosts] = useState<SavedPost[]>([]);
  const [viewingSavedPost, setViewingSavedPost] = useState<SavedPost | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [copyStatus, setCopyStatus] = useState<{[key: string]: boolean}>({});
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [trendingTopics, setTrendingTopics] = useState<string[]>([]);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);

  // Load saved posts
  useEffect(() => {
    const stored = localStorage.getItem('queen_of_clean_saved');
    if (stored) {
      try {
        setSavedPosts(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to load saved posts", e);
      }
    }
  }, []);

  // Save posts
  useEffect(() => {
    localStorage.setItem('queen_of_clean_saved', JSON.stringify(savedPosts));
  }, [savedPosts]);

  // Fetch trending topics
  useEffect(() => {
    const fetchTrends = async () => {
      setIsLoadingTrends(true);
      try {
        const topics = await getTrendingTopics(settings.category);
        setTrendingTopics(topics);
      } catch (e) {
        console.error("Trend fetch error", e);
      } finally {
        setIsLoadingTrends(false);
      }
    };
    fetchTrends();
  }, [settings.category]);

  const stripFormatting = (text: string) => {
    if (!text) return "";
    return text.replace(/\*\*/g, '').replace(/__/g, '').replace(/#/g, '').replace(/\*/g, '•').trim();
  };

  const handleGenerate = async () => {
    if (!settings.topic) return;

    setIsGenerating(true);
    try {
      const result = await generateBlogContent(settings, PRODUCTS);
      
      // Only strip formatting if it's not already markdown we want to keep
      // Actually, the user wants to embed links, so we should keep markdown
      // result.content = stripFormatting(result.content);
      result.title = stripFormatting(result.title);
      result.summary = stripFormatting(result.summary);
      
      setBlog(result);
      setIsEditing(false);
      setViewingSavedPost(null);
      setTimeout(() => {
        document.getElementById('generation-result')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error: any) {
      console.error("Generation error details:", error);
      alert(`Oh dear! We had a little hiccup, darling! ${error.message || ''}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = () => {
    if (!blog) return;
    const newSaved: SavedPost = {
      ...blog,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      type: settings.category
    };
    setSavedPosts([newSaved, ...savedPosts]);
    alert("Saved to your collection, darling!");
  };

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopyStatus({ ...copyStatus, [key]: true });
    setTimeout(() => setCopyStatus({ ...copyStatus, [key]: false }), 2000);
  };

  const copyRichText = async (html: string, key: string) => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const data = [new ClipboardItem({ 'text/html': blob })];
      await navigator.clipboard.write(data);
      setCopyStatus({ ...copyStatus, [key]: true });
      setTimeout(() => setCopyStatus({ ...copyStatus, [key]: false }), 2000);
    } catch (err) {
      // Fallback to plain text if rich text copy fails
      const text = html.replace(/<[^>]*>/g, '');
      copyToClipboard(text, key);
    }
  };

  const downloadImage = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const insertLink = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    const url = prompt("Enter the URL, darling:", "https://");
    if (url) {
      const newContent = textarea.value.substring(0, start) + `[${selectedText || 'link'}](${url})` + textarea.value.substring(end);
      if (viewingSavedPost) {
        setViewingSavedPost({ ...viewingSavedPost, content: newContent });
        setSavedPosts(savedPosts.map(p => p.id === viewingSavedPost.id ? { ...p, content: newContent } : p));
      } else if (blog) {
        setBlog({ ...blog, content: newContent });
      }
      
      // Reset focus and selection
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + 1, start + 1 + (selectedText || 'link').length);
      }, 0);
    }
  };

  const generateAffiliateHTML = (product: Product) => {
    return `<div style="padding: 24px; border: 2px solid #F1B1BA; border-radius: 24px; text-align: center; font-family: 'Inter', sans-serif; margin: 24px 0; background: #fff; box-shadow: 0 10px 30px rgba(0,0,0,0.05);">
  <img src="${product.image}" style="width: 120px; height: 120px; object-fit: contain; margin-bottom: 16px;">
  <h4 style="margin: 0; font-weight: 900; text-transform: uppercase; font-size: 14px; letter-spacing: 0.5px; color: #000;">${product.name}</h4>
  <p style="margin: 12px 0; font-size: 20px; font-weight: 900; color: #000;">£${product.price.toFixed(2)}</p>
  <a href="${product.url}" style="display: inline-block; padding: 14px 34px; background: #F1B1BA; color: #000; text-decoration: none; border-radius: 50px; font-weight: 900; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; transition: 0.3s;">Shop Now</a>
</div>`;
  };

  const filteredProducts = useMemo(() => {
    return PRODUCTS.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            p.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const renderPost = (post: BlogPost | SavedPost) => (
    <div className="animate-slideUp space-y-12 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        {viewingSavedPost && (
          <button onClick={() => setViewingSavedPost(null)} className="flex items-center space-x-2 px-6 py-2.5 bg-white border border-slate-200 rounded-full text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-black">
            <i className="fas fa-arrow-left"></i>
            <span>Back to Collection</span>
          </button>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3 ml-auto">
          {!viewingSavedPost && (
            <button onClick={handleSave} className="flex items-center space-x-3 px-6 py-3.5 bg-white text-black rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all border-2 border-slate-200 hover:border-[#F1B1BA] shadow-sm">
              <i className="fas fa-heart"></i>
              <span>Save Post</span>
            </button>
          )}
          <button onClick={() => setShowPublishModal(true)} className="flex items-center space-x-3 px-6 py-3.5 bg-black text-white rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all border-2 border-black hover:bg-slate-800 shadow-xl">
            <i className="fab fa-squarespace"></i>
            <span>Publish To Squarespace</span>
          </button>
          <button onClick={() => setIsEditing(!isEditing)} className={`flex items-center space-x-3 px-6 py-3.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] transition-all border-2 ${isEditing ? 'bg-white border-black text-black' : 'bg-slate-100 border-transparent text-slate-500 hover:bg-slate-200'}`}>
            <i className={`fas ${isEditing ? 'fa-check' : 'fa-pen-to-square'}`}></i>
            <span>{isEditing ? 'Save Edits' : 'Edit Post'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-12">
        <div className="xl:col-span-2 space-y-12">
            <div className="bg-white rounded-[3rem] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.06)] border border-slate-100 overflow-hidden">
            {post.featuredImage && (
              <div className="relative h-[450px] w-full bg-slate-50">
                <img src={post.featuredImage} alt="Hero" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-10 md:p-16">
              <h1 className="font-black text-black uppercase tracking-tighter leading-tight text-3xl md:text-5xl mb-6">{post.title}</h1>
              <p className="text-slate-500 font-medium italic text-lg leading-relaxed mb-8">{post.summary}</p>
              <div className="mt-6 border-t border-slate-100 pt-8">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Edit Content (Markdown supported)</label>
                      <button 
                        onClick={insertLink}
                        className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-[#F1B1BA] rounded-full text-[9px] font-black uppercase transition-all"
                      >
                        <i className="fas fa-link"></i>
                        <span>Insert Link</span>
                      </button>
                    </div>
                    <textarea
                      ref={textareaRef}
                      className="w-full h-[500px] p-8 bg-slate-50 border border-slate-200 rounded-[2rem] text-lg font-serif text-black outline-none focus:border-[#F1B1BA] shadow-inner transition-all"
                      value={post.content}
                      onChange={(e) => {
                        const newContent = e.target.value;
                        if (viewingSavedPost) {
                          setViewingSavedPost({ ...viewingSavedPost, content: newContent });
                          setSavedPosts(savedPosts.map(p => p.id === viewingSavedPost.id ? { ...p, content: newContent } : p));
                        } else if (blog) {
                          setBlog({ ...blog, content: newContent });
                        }
                      }}
                    />
                    <p className="text-[10px] text-slate-400 italic">Tip: Highlight text and click "Insert Link" to put a link behind words, darling!</p>
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-[2rem] p-10 text-xl leading-relaxed font-serif text-black shadow-inner markdown-body">
                    <ReactMarkdown>{post.content}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Social Kit at the bottom */}
          <div className="bg-white p-10 md:p-16 rounded-[3rem] border border-slate-100 shadow-sm space-y-8">
            <div className="flex items-center space-x-3">
              <i className="fas fa-share-nodes text-[#F1B1BA]"></i>
              <h3 className="font-black text-[14px] uppercase tracking-[0.2em]">Social Media Kit</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['instagram', 'facebook', 'tiktok'].map(platform => (
                <div key={platform} className="space-y-4 bg-slate-50 p-6 rounded-3xl border border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <i className={`fab fa-${platform === 'tiktok' ? 'tiktok' : platform} text-slate-400`}></i>
                      <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">{platform}</span>
                    </div>
                    <button 
                      onClick={() => copyToClipboard((post.socialPosts as any)?.[platform] || '', platform)} 
                      className="text-[10px] font-black uppercase text-[#F1B1BA] hover:text-black transition-colors"
                    >
                      {copyStatus[platform] ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <div className="text-[12px] text-slate-600 leading-relaxed italic line-clamp-6">
                    {(post.socialPosts as any)?.[platform]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="bg-white p-8 rounded-[3rem] border border-slate-100 shadow-sm space-y-8">
            <div className="flex items-center space-x-3">
              <i className="fas fa-sack-dollar text-[#F1B1BA]"></i>
              <h3 className="font-black text-[11px] uppercase tracking-[0.2em]">Affiliate Kit</h3>
            </div>
            <div className="space-y-4">
              {post.suggestedProducts.map(product => (
                <div key={product.id} className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-3">
                  <div className="flex items-center space-x-3">
                    <img src={product.image} className="w-10 h-10 object-contain" />
                    <span className="text-[10px] font-black uppercase leading-tight line-clamp-1">{product.name}</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => copyToClipboard(product.url, `url-${product.id}`)}
                      className="flex-1 py-2 bg-white text-[9px] font-black uppercase rounded-full border border-slate-200 hover:bg-[#F1B1BA] transition-all"
                    >
                      {copyStatus[`url-${product.id}`] ? 'Link Copied!' : 'Copy Link'}
                    </button>
                    <button 
                      onClick={() => copyToClipboard(generateAffiliateHTML(product), `html-${product.id}`)}
                      className="flex-1 py-2 bg-black text-white text-[9px] font-black uppercase rounded-full hover:bg-[#F1B1BA] hover:text-black transition-all"
                    >
                      {copyStatus[`html-${product.id}`] ? 'HTML Copied!' : 'Shop Block'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFCFD] flex flex-col font-inter text-black">
      <Header onHome={() => { setBlog(null); setActiveTab('studio'); }} />

      <main className="flex-1 max-w-7xl mx-auto w-full p-8 md:p-12 space-y-12">
        <div className="flex justify-center">
          <div className="bg-white border border-slate-200 p-1 rounded-full flex items-center shadow-sm">
            <button 
              onClick={() => { setActiveTab('studio'); setViewingSavedPost(null); }} 
              className={`px-8 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'studio' ? 'bg-[#F1B1BA] text-black shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Content Studio
            </button>
            <button 
              onClick={() => setActiveTab('saved')} 
              className={`px-8 py-2.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'saved' ? 'bg-[#F1B1BA] text-black shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
            >
              Saved Collection ({savedPosts.length})
            </button>
          </div>
        </div>

        {activeTab === 'studio' ? (
          <section className="animate-fadeIn space-y-12">
            <div className="max-w-6xl mx-auto w-full">
              <div className="bg-white p-12 rounded-[4rem] border border-slate-200 shadow-sm space-y-12">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-1.5 h-6 bg-[#F1B1BA] rounded-full"></div>
                    <h2 className="text-[14px] font-black text-black uppercase tracking-[0.3em]">Content Creation</h2>
                  </div>
                </div>

                <div className="space-y-10">
                  {/* Platform Options */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Platform</label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { id: 'Cleaning', label: 'Cleaning Guide' },
                        { id: 'Recipe', label: 'Recipe' },
                        { id: 'Social', label: 'Social Media Only' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setSettings({...settings, category: opt.id as any})}
                          className={`px-8 py-3 rounded-full text-[11px] font-black uppercase tracking-widest border transition-all hover:scale-105 active:scale-95 ${settings.category === opt.id ? 'bg-[#F1B1BA] border-[#F1B1BA] text-black shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-pink-200'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Angle Options */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Target Angle</label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { id: 'Eco-Friendly', label: 'Eco-Friendly Focus' },
                        { id: 'Budget', label: 'Budget Conscious' },
                        { id: 'Speed Clean', label: 'Speed Clean Hacks' },
                        { id: 'Deep Dive', label: 'Expert Masterclass' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setSettings({...settings, angle: opt.id as any})}
                          className={`px-8 py-3 rounded-full text-[11px] font-black uppercase tracking-widest border transition-all hover:scale-105 active:scale-95 ${settings.angle === opt.id ? 'bg-[#F1B1BA] border-[#F1B1BA] text-black shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-pink-200'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Format Options */}
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Article Format</label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { id: 'Short', label: 'Micro Post' },
                        { id: 'Medium', label: 'Standard Blog' },
                        { id: 'Long', label: 'In-Depth Guide' },
                        { id: 'Top Tips', label: 'The Top 10 List' }
                      ].map(opt => (
                        <button
                          key={opt.id}
                          onClick={() => setSettings({...settings, length: opt.id as any})}
                          className={`px-8 py-3 rounded-full text-[11px] font-black uppercase tracking-widest border transition-all hover:scale-105 active:scale-95 ${settings.length === opt.id ? 'bg-[#F1B1BA] border-[#F1B1BA] text-black shadow-lg' : 'bg-white border-slate-100 text-slate-400 hover:border-pink-200'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1 flex items-center gap-2">
                    <i className="fas fa-sparkles text-[#F1B1BA]"></i>
                    <span>Trending SEO Ideas</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {trendingTopics.map((trend, idx) => (
                      <button key={idx} onClick={() => setSettings({ ...settings, topic: trend })} className={`px-6 py-2.5 rounded-full text-[11px] font-bold transition-all border hover:scale-105 active:scale-95 ${settings.topic === trend ? 'bg-[#F1B1BA] border-[#F1B1BA] text-black shadow-lg' : 'bg-white border-slate-100 text-slate-500 hover:border-pink-100'}`}>
                        {trend}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <textarea 
                    placeholder="What are we cleaning today, darling? (e.g., Removing limescale from taps)"
                    className="w-full p-8 bg-slate-50 border border-slate-200 rounded-[2.5rem] h-32 focus:border-[#F1B1BA] outline-none text-lg font-medium shadow-inner transition-all"
                    value={settings.topic}
                    onChange={(e) => setSettings({...settings, topic: e.target.value})}
                  />
                </div>

                <div className="flex justify-center">
                  <button 
                    onClick={handleGenerate} 
                    disabled={isGenerating || !settings.topic} 
                    className={`h-[80px] rounded-full font-black text-xs uppercase tracking-[0.4em] transition-all hover:scale-[1.02] active:scale-[0.98] ${isGenerating ? 'bg-slate-100 text-slate-400 w-72' : 'bg-[#F1B1BA] text-black w-full md:w-[500px] shadow-[0_20px_40px_-10px_rgba(241,177,186,0.5)]'}`}
                  >
                    {isGenerating ? 'Polishing Post...' : 'Create Post'}
                  </button>
                </div>
              </div>
            </div>

            <section id="generation-result" className="pb-24">
              {isGenerating ? (
                 <div className="max-w-6xl mx-auto min-h-[500px] bg-white rounded-[4rem] p-20 border border-slate-100 flex flex-col items-center justify-center space-y-8 animate-pulse">
                    <div className="w-20 h-20 border-4 border-[#F1B1BA] border-t-transparent rounded-full animate-spin"></div>
                    <h3 className="text-xl font-black text-black uppercase tracking-tighter">Creating for you...</h3>
                 </div>
              ) : blog ? renderPost(blog) : null}
            </section>
          </section>
        ) : (
          <section className="animate-fadeIn pb-24 space-y-8">
            {viewingSavedPost ? renderPost(viewingSavedPost) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {savedPosts.map((post) => (
                  <div key={post.id} onClick={() => setViewingSavedPost(post)} className="group bg-white rounded-[2.5rem] border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl cursor-pointer transition-all">
                    <div className="relative h-48 bg-slate-50">
                      {post.featuredImage && <img src={post.featuredImage} className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-8">
                      <h3 className="text-lg font-black text-black uppercase tracking-tight line-clamp-2">{post.title}</h3>
                      <p className="text-slate-400 text-[10px] font-bold uppercase mt-4">{new Date(post.timestamp).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <footer className="py-16 border-t border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-10 text-center">
          <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
            &copy; {new Date().getFullYear()} Queen of Clean. Studio Hub.
          </p>
        </div>
      </footer>

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[3rem] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-scaleIn">
            <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-[#F1B1BA]">
              <h3 className="font-black text-black uppercase tracking-widest">Publish to Squarespace</h3>
              <button onClick={() => setShowPublishModal(false)} className="text-black hover:scale-110 transition-transform">
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar">
              {/* Step 1: Image */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-[10px] font-black">1</span>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-black">Featured Image</h4>
                  </div>
                  {(blog?.featuredImage || viewingSavedPost?.featuredImage) && (
                    <button 
                      onClick={() => downloadImage(blog?.featuredImage || viewingSavedPost?.featuredImage || '', 'queen-of-clean-hero.png')}
                      className="text-[10px] font-black uppercase text-black bg-[#F1B1BA] px-4 py-2 rounded-full hover:bg-black hover:text-white transition-all"
                    >
                      Download Image
                    </button>
                  )}
                </div>
                <div className="aspect-video rounded-2xl overflow-hidden border border-slate-100 bg-slate-50">
                  <img src={blog?.featuredImage || viewingSavedPost?.featuredImage} className="w-full h-full object-cover" />
                </div>
              </div>

              {/* Step 2: Headline */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-[10px] font-black">2</span>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-black">Blog Headline</h4>
                  </div>
                  <button 
                    onClick={() => copyToClipboard(blog?.title || viewingSavedPost?.title || '', 'copy-headline')}
                    className="text-[10px] font-black uppercase text-black bg-[#F1B1BA] px-4 py-2 rounded-full hover:bg-black hover:text-white transition-all"
                  >
                    {copyStatus['copy-headline'] ? 'Copied!' : 'Copy Headline'}
                  </button>
                </div>
                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 font-black text-lg uppercase leading-tight">
                  {blog?.title || viewingSavedPost?.title}
                </div>
              </div>

              {/* Step 3: Body */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-6 h-6 bg-black text-white rounded-full flex items-center justify-center text-[10px] font-black">3</span>
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-black">Body Content</h4>
                  </div>
                  <button 
                    onClick={() => copyRichText(marked.parse(blog?.content || (viewingSavedPost?.content || '')) as string, 'copy-body')}
                    className="text-[10px] font-black uppercase text-black bg-[#F1B1BA] px-4 py-2 rounded-full hover:bg-black hover:text-white transition-all"
                  >
                    {copyStatus['copy-body'] ? 'Copied!' : 'Copy Body Text'}
                  </button>
                </div>
                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 max-h-48 overflow-y-auto custom-scrollbar">
                  <div className="prose prose-sm font-serif text-slate-600 leading-relaxed markdown-body">
                    <ReactMarkdown>{blog?.content || (viewingSavedPost?.content || '')}</ReactMarkdown>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 italic text-center">"This copies the text and your links perfectly, darling!"</p>
              </div>
            </div>
            <div className="p-8 bg-slate-50 border-t border-slate-100 text-center">
              <p className="text-[11px] text-slate-500 italic">"Pasting into Squarespace is like a fresh coat of polish—everything just sparkles!"</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        button { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important; }
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #f1f5f9; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #F1B1BA; }
        
        .markdown-body a { color: #F1B1BA; text-decoration: underline; font-weight: bold; }
        .markdown-body ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
        .markdown-body ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 1rem; }
        .markdown-body p { margin-bottom: 1rem; }
        .markdown-body h1, .markdown-body h2, .markdown-body h3 { font-weight: bold; margin-top: 1.5rem; margin-bottom: 1rem; }
      `}</style>
    </div>
  );
};

export default App;
